import { defineStore } from 'pinia';

import { logout, tokenLogin, user } from '@/api/auth';
import { getEntryListList } from '@/api/dictionary';
import { getSettings, saveSettings } from '@/api/settings';
import { UnauthorizedError } from '@/http/errors';
import { EntryList, Settings, User } from '@/types';

interface State {
	authenticated?: boolean;
	user: User | null;
	settings: Settings;
	entryLists: EntryList[] | null;
}

export const useUserStore = defineStore('user', {
	state: (): State => ({
		authenticated: undefined,
		entryLists: null,
		user: null,
		settings: {
			locale: 'la',
		},
	}),
	getters: {
		isAuthenticated: state => !!state.authenticated,
	},
	actions: {
		async getPrivateEntryLists() {
			if (this.entryLists !== null) return this.entryLists;

			await this.forceLoadPriveEntryLists();
			if (this.entryLists === null) return <EntryList[]>{};

			return this.entryLists;
		},
		async forceLoadPriveEntryLists() {
			return getEntryListList('private')
				.then(lists => {
					this.entryLists = lists.items;
					return lists.items;
				})
				.catch((err: Error) => {
					if (err instanceof UnauthorizedError) {
						this.entryLists = null;
						return null;
					}
					throw err;
				});
		},
		async tokenLogin(token: string, active_school_id: string) {
			return tokenLogin(token, active_school_id).then(user => {
				this.user = user;
				this.authenticated = true;
				return user;
			});
		},
		async logout() {
			return logout().then(() => {
				this.authenticated = false;
				this.user = null;
				this.entryLists = null;
				return;
			});
		},
		async getUser() {
			return user()
				.then(user => {
					this.user = user;
					this.authenticated = true;
					return user;
				})
				.catch((err: Error) => {
					if (err instanceof UnauthorizedError) {
						this.authenticated = false;
						this.user = null;
						return null;
					}
					throw err;
				});
		},
		async getSettings() {
			return getSettings().then(settings => {
				this.settings = settings;
				return settings;
			});
		},
		async saveSettings(settings: Settings) {
			return saveSettings(settings).then(settings => {
				this.settings = settings;
				return settings;
			});
		},
	},
});
